import FilterListIcon from "@mui/icons-material/FilterList";
import { Box, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type FC, type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	SystemReactTinyPopover,
	SystemReactTinyPopoverChildrenWrapperStyle,
	SystemReactTinyPopoverRootStyle,
} from "../../../../components/UIToolkit/Popups/SystemReactTinyPopover";
import SystemButton from "../../../../components/UIToolkit/SystemButton/SystemButton";
import { useChosenDepartment } from "../../../../contexts/ChosenDepartment";
import { ALPHA, FONT_SIZE, INK } from "../../../../theme/tokens";
import { useHiringAbsenceSettings, useSaveHiringAbsenceSettings } from "../hooks/useHiringAbsenceSettings";
import { countedAbsenceItems } from "../utils/absenceSettings";
import AbsenceSettingsPanel from "./AbsenceSettingsPanel";

// The panel lays out its own header, body and footer insets, so the popover root adds none.
const PANEL_ROOT_STYLE = { ...SystemReactTinyPopoverRootStyle, padding: 0 };
// Flex, not the default block: a block around the inline-flex metric group adds a line box's descender below it,
// 6px that lifted the Absences switches above the other groups' (the toggle bar aligns them by their bottom edge).
const TRIGGER_WRAPPER_STYLE = { ...SystemReactTinyPopoverChildrenWrapperStyle, display: "flex" };

/**
 * The Absences caption + switches in the Recurring Gaps toggle bar, with the tooltip that says how many items
 * Absences counts and opens the "What counts as an absence?" checklist from there (DAT-850).
 */
const AbsencesMetricHeader: FC<{ children: ReactElement }> = ({ children }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useChosenDepartment();
	const { data, isLoading, isError } = useHiringAbsenceSettings();
	const save = useSaveHiringAbsenceSettings();
	const [open, setOpen] = useState(false);
	const [tooltipOpen, setTooltipOpen] = useState(false);

	const status = (() => {
		if (isLoading) return t("hiring.absenceSettings.loading");
		if (isError || !data) return t("hiring.absenceSettings.loadFailed");
		const reasonDecisions = data.reasons.map((reason) => reason.countsAsAbsence);
		return t(
			"hiring.absenceSettings.counting",
			countedAbsenceItems({ countsPto: data.countsPto, countsReplaceRequests: data.countsReplaceRequests, reasonDecisions }),
		);
	})();

	const tooltip = (
		<Box data-testid="hiring-gaps-absences-tooltip">
			<Typography sx={{ fontSize: FONT_SIZE.CAPTION }}>{t("hiring.gaps.absencesTooltip")}</Typography>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 1, pt: 1, borderTop: `1px solid ${alpha(INK.ON_DARK, ALPHA.CONTROL_TINT_HOVER)}` }}>
				<Typography sx={{ flex: 1, fontSize: FONT_SIZE.CAPTION, color: alpha(INK.ON_DARK, ALPHA.MUTED_SECONDARY) }} data-testid="hiring-gaps-absences-counting">
					{status}
				</Typography>
				<SystemButton
					size="compact"
					noFullWidthMobile
					leadingIcon={<FilterListIcon sx={{ fontSize: 14 }} />}
					leadingIconGap="6px"
					disabled={!data}
					aria-haspopup="dialog"
					aria-expanded={open}
					onClick={() => {
						setTooltipOpen(false);
						setOpen(true);
					}}
					data-testid="hiring-gaps-absences-customize"
				>
					{t("hiring.absenceSettings.customize")}
				</SystemButton>
			</Box>
		</Box>
	);

	return (
		<SystemReactTinyPopover
			isOpen={open && data !== undefined}
			onClickOutside={() => setOpen(false)}
			onClose={() => setOpen(false)}
			closeOnEscape
			fitViewport
			positions={["bottom", "top", "left"]}
			align="end"
			contentRootStyle={PANEL_ROOT_STYLE}
			childrenWrapperStyle={TRIGGER_WRAPPER_STYLE}
			content={
				data ? (
					<AbsenceSettingsPanel
						settings={data}
						departmentId={chosenDepartment.department_id}
						saving={save.isPending}
						onSave={(update) => save.mutate(update, { onSuccess: () => setOpen(false) })}
						onClose={() => setOpen(false)}
					/>
				) : (
					<Box />
				)
			}
		>
			{/* Hidden while the panel is open: it would sit over the panel it just opened. */}
			<Tooltip title={tooltip} placement="top" arrow open={tooltipOpen && !open} onOpen={() => setTooltipOpen(true)} onClose={() => setTooltipOpen(false)}>
				{children}
			</Tooltip>
		</SystemReactTinyPopover>
	);
};

export default AbsencesMetricHeader;
